import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { getStatus } from 'components/orderManagement'
import { convertTo12HourFormat } from 'utils/helpers'
import { roundToDecimals } from 'utils/helpers/round'

export type FocFormData = {
  locationCode: string
  appliesToMagentoOrderNo: string
  barcodeNo: string
  lineAmountExclVAT: number
  lineAmountInclVAT: number
  vatAmount: number
  itemDesc?: string
  itemUom?: string
  quantity: number
  orderType: string
}

export type OrderItem = {
  lineNo: number
  itemType: string
  barcodeNo: string
  lineAmountExclVAT: number
  lineAmountInclVAT: number
  vatAmount: number
  quantity: number
  unitPrice: number
  discountAmount: number
}
export const FILTER_FIELD_FOC_ORDERS = 'focOrderFilters'

export const FOC_FORM_FIELDS = {
  ORDER_NO: { key: 'orderNo', label: 'Order Number' },
  ORDER_TYPE: { key: 'orderType', label: 'Type' },
  DATE: { key: 'createdAt', label: 'Date' },
  LOCATION_CODE: { key: 'locationCode', label: 'Location Code' },
  STORE_NAME: { key: 'storeName', label: 'Store Name' },
  ITEMS: { key: 'items', label: 'Items' },
  BARCODE_NO: { key: 'barcodeNo', label: 'Barcode No' },
  QUANTITY: { key: 'quantity', label: 'Quantity' },
  UNIT_PRICE: { key: 'unitPrice', label: 'Unit Price' },
  DISCOUNT_AMOUNT: { key: 'discountAmount', label: 'Discount Amount' },
  LINE_AMOUNT_EXCL_VAT: {
    key: 'lineAmountExclVAT',
    label: 'Unit Line Amount Excl VAT',
  },
  VAT_AMOUNT: { key: 'vatAmount', label: 'Unit Vat Amount' },
  LINE_AMOUNT_INCL_VAT: {
    key: 'lineAmountInclVAT',
    label: 'Unit Line Amount Incl VAT',
  },
  TOTAL_AMOUNT_EXCL_VAT: {
    key: 'totalAmountExclVAT',
    label: 'Total Amount Excl VAT',
  },
  TOTAL_VAT_AMOUNT: { key: 'totalVATAmount', label: 'Total VAT Amount' },
  TOTAL_AMOUNT_INCL_VAT: {
    key: 'totalAmountInclVAT',
    label: 'Total Amount Incl VAT',
  },
  APPLIES_TO_MAGENTO_ORDER_NO: {
    key: 'appliesToMagentoOrderNo',
    label: 'Magento/Aggregator Order No',
  },
  ORDER_CREATED_BY: { key: 'createdBy', label: 'Created By' },

  ERP_ORDER_SYNC: { key: 'orderErpSync', label: 'Erp Order Sync' },
  RECON_ERP_SYNC: { key: 'orderReconErpSync', label: 'Recon ERP Sync' },

  ITEM_DESCRIPTION: { key: 'itemDesc', label: 'Item Description' },
  ITEM_UOM: { key: 'itemUom', label: 'Item UOM' },
} as const

export const columns = [
  {
    headerName: FOC_FORM_FIELDS.ORDER_TYPE.label,
    field: FOC_FORM_FIELDS.ORDER_TYPE.key,
    minWidth: 150,
  },
  {
    headerName: FOC_FORM_FIELDS.ORDER_NO.label,
    field: FOC_FORM_FIELDS.ORDER_NO.key,
    minWidth: 150,
  },
  {
    headerName: FOC_FORM_FIELDS.LOCATION_CODE.label,
    field: FOC_FORM_FIELDS.LOCATION_CODE.key,
    minWidth: 150,
  },
  {
    headerName: FOC_FORM_FIELDS.STORE_NAME.label,
    field: FOC_FORM_FIELDS.STORE_NAME.key,
    minWidth: 150,
  },
  {
    headerName: FOC_FORM_FIELDS.APPLIES_TO_MAGENTO_ORDER_NO.label,
    field: FOC_FORM_FIELDS.APPLIES_TO_MAGENTO_ORDER_NO.key,
    minWidth: 230,
  },
  {
    headerName: FOC_FORM_FIELDS.DATE.label,
    field: FOC_FORM_FIELDS.DATE.key,
    minWidth: 170,
    valueFormatter: (params: ValueFormatterParams) =>
      convertTo12HourFormat(params.value),
  },
  {
    headerName: FOC_FORM_FIELDS.TOTAL_AMOUNT_EXCL_VAT.label,
    field: FOC_FORM_FIELDS.TOTAL_AMOUNT_EXCL_VAT.key,
    minWidth: 200,
    valueFormatter: (params: ValueFormatterParams) =>
      roundToDecimals(params.value, 4),
  },
  {
    headerName: FOC_FORM_FIELDS.TOTAL_VAT_AMOUNT.label,
    field: FOC_FORM_FIELDS.TOTAL_VAT_AMOUNT.key,
    minWidth: 200,
  },
  {
    headerName: FOC_FORM_FIELDS.TOTAL_AMOUNT_INCL_VAT.label,
    field: FOC_FORM_FIELDS.TOTAL_AMOUNT_INCL_VAT.key,
    minWidth: 200,
  },

  {
    headerName: FOC_FORM_FIELDS.ORDER_CREATED_BY.label,
    field: FOC_FORM_FIELDS.ORDER_CREATED_BY.key,
    minWidth: 200,
    valueFormatter: (params) => {
      const value = params?.value
      if (!value) return ''

      return value
        .toLowerCase()
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
  },
  {
    headerName: FOC_FORM_FIELDS.ERP_ORDER_SYNC.label,
    field: FOC_FORM_FIELDS.ERP_ORDER_SYNC.key,
    minWidth: 150,
    cellRenderer: (params: ICellRendererParams) => {
      return (
        <span
          className={`rounded-lg px-2 py-1 text-sm ${
            params?.value ? 'bg-green-400 text-black' : 'bg-red-500  text-white'
          }`}
        >
          {getStatus(params?.value)}
        </span>
      )
    },
  },
  {
    headerName: FOC_FORM_FIELDS.RECON_ERP_SYNC.label,
    field: FOC_FORM_FIELDS.RECON_ERP_SYNC.key,
    minWidth: 150,
    cellRenderer: (params: ICellRendererParams) => {
      return (
        <span
          className={`rounded-lg px-2 py-1 text-sm ${
            params?.value ? 'bg-green-400 text-black' : 'bg-red-500  text-white'
          }`}
        >
          {getStatus(params?.value)}
        </span>
      )
    },
  },
] as ColDef[]
